// useKeyboardShortcuts — installs the global chrome keyboard handlers.
//
// Mounted ONCE inside PageLayout (which itself only renders for
// authenticated routes — public pages get no shortcuts). It watches
// document keydown events and dispatches into:
//   * ChromeContext (open search, toggle sidebar, open cheat sheet)
//   * react-router's navigate() for G-prefix go-to keys
//   * an external "open preferences" handler — currently routed to
//     /preferences via navigate as well
//
// All the routing rules + the typing-surface guard live in
// keyboard-shortcuts-helpers.ts so they can be unit-tested without a
// DOM.

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useChrome } from "../components/ChromeContext";
import {
  eventTargetsTypingSurface,
  GO_TO_ROUTES,
  G_PREFIX_TIMEOUT_MS,
  isGPrefixStart,
  resolveGlobalShortcut,
  resolveGoToKey,
  type ShortcutKeyEvent,
} from "./keyboard-shortcuts-helpers";

function asShortcutEvent(event: KeyboardEvent): ShortcutKeyEvent {
  const target = event.target as Element | null;
  return {
    key: event.key,
    metaKey: event.metaKey,
    ctrlKey: event.ctrlKey,
    altKey: event.altKey,
    shiftKey: event.shiftKey,
    target: target
      ? {
          tagName: target.tagName,
          isContentEditable: (target as HTMLElement).isContentEditable,
        }
      : null,
  };
}

export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const chrome = useChrome();
  const gPrefixActiveRef = useRef(false);
  const gPrefixTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function clearGPrefix() {
      gPrefixActiveRef.current = false;
      if (gPrefixTimerRef.current) {
        clearTimeout(gPrefixTimerRef.current);
        gPrefixTimerRef.current = null;
      }
    }

    function startGPrefix() {
      gPrefixActiveRef.current = true;
      if (gPrefixTimerRef.current) clearTimeout(gPrefixTimerRef.current);
      gPrefixTimerRef.current = setTimeout(
        clearGPrefix,
        G_PREFIX_TIMEOUT_MS,
      );
    }

    function handler(rawEvent: KeyboardEvent) {
      const event = asShortcutEvent(rawEvent);

      // Mid-G-prefix: try to resolve the second key BEFORE the typing
      // guard, because the prefix only fires when the user is NOT in a
      // typing surface (we wouldn't have started it otherwise).
      if (gPrefixActiveRef.current) {
        const dest = resolveGoToKey(event.key);
        clearGPrefix();
        if (dest) {
          rawEvent.preventDefault();
          void navigate(GO_TO_ROUTES[dest]);
          return;
        }
        // Fall through — the second key wasn't a known go-to letter,
        // treat it as a normal keystroke.
      }

      // Modifier-bearing shortcuts can fire even while typing — Cmd+K
      // is unambiguously chrome. The typing guard only blocks
      // bare-key shortcuts.
      const isCmd = event.metaKey || event.ctrlKey;
      if (!isCmd && eventTargetsTypingSurface(event)) {
        return;
      }

      const action = resolveGlobalShortcut(event);
      if (action) {
        switch (action.kind) {
          case "open_ask_stew":
            rawEvent.preventDefault();
            chrome.openAskStew();
            return;
          case "toggle_sidebar":
            rawEvent.preventDefault();
            chrome.toggleSidebar();
            return;
          case "open_preferences":
            rawEvent.preventDefault();
            void navigate("/preferences");
            return;
          case "open_cheat_sheet":
            rawEvent.preventDefault();
            chrome.openCheatSheet();
            return;
          case "close_overlay":
            // Esc — let the focused overlay handle it via its own
            // listener; only close the cheat sheet / Stew panel at
            // the chrome level when something is open and nothing
            // else claims it. (The Drawer primitive also handles
            // Esc internally, so this is a fallback.)
            if (chrome.isCheatSheetOpen) {
              rawEvent.preventDefault();
              chrome.closeCheatSheet();
              return;
            }
            if (chrome.isAskStewOpen) {
              rawEvent.preventDefault();
              chrome.closeAskStew();
              return;
            }
            // The left rail's second pane (2026-08-24 audit finding #4:
            // "Escape doesn't close the panel" — it already worked on the
            // mobile drawer via the Drawer primitive above, but nothing
            // wired it for the desktop pane; ⌘/ was the only way to shut
            // it, and Escape is the reflex people actually reach for).
            // Lowest priority: it never fires while a true overlay
            // (cheat sheet, Ask Stew) is still up.
            if (!chrome.isSidebarCollapsed) {
              rawEvent.preventDefault();
              chrome.closeNavSection();
              return;
            }
            return;
        }
      }

      // Bare G in a non-typing surface starts the prefix window.
      if (isGPrefixStart(event)) {
        rawEvent.preventDefault();
        startGPrefix();
      }
    }

    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
      clearGPrefix();
    };
  }, [chrome, navigate]);
}
